import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RedirectCommand, ResolveFn, Router } from '@angular/router';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { semToastDeErro } from './interceptors/error.interceptor';

/**
 * Verifica se o `:id` da URL corresponde a um registro que o usuário pode ver,
 * antes de abrir a tela.
 *
 * Sem isso, um ID inexistente ou de outro cliente abre a tela normalmente e ela
 * fica em branco, sem explicar nada. Aqui o erro vira destino:
 * 404 (inexistente) e 405 (recurso sem endpoint de leitura) → `/nao-encontrado`;
 * 403 (existe, mas fora do escopo do usuário) → `/sem-acesso`.
 *
 * Depende de `data.recurso` na rota: o segmento da API correspondente, que nem
 * sempre coincide com o caminho da UI (ex.: a tela `pessoas` lê `funcionario`).
 * Rota com `:id` e sem `recurso` é ignorada — o resolver não bloqueia a
 * navegação por falta de configuração.
 *
 * Os erros de rede/servidor (0, 5xx) também são deixados passar: são
 * transitórios e a própria tela os reporta, então derrubar a navegação por
 * causa deles trocaria uma falha temporária por um destino errado.
 *
 * O redirecionamento sai como `RedirectCommand`: um `UrlTree` devolvido por um
 * resolver não redireciona — vira apenas dado resolvido, e a navegação segue.
 */
export const registroExistenteResolver: ResolveFn<boolean | RedirectCommand> = (
  route: ActivatedRouteSnapshot,
): Observable<boolean | RedirectCommand> => {
  const http = inject(HttpClient);
  const router = inject(Router);

  const recurso = route.data['recurso'] as string | undefined;
  const id = route.paramMap.get('id');

  if (!recurso || !id) return of(true);

  return http
    .get(`${environment.apiUrl}/api/${recurso}/${encodeURIComponent(id)}`, {
      context: semToastDeErro(),
    })
    .pipe(
      map(() => true),
      catchError((erro: HttpErrorResponse) => {
        if (erro.status === 403) return of(new RedirectCommand(router.createUrlTree(['/sem-acesso'])));
        if (erro.status === 404 || erro.status === 405) {
          return of(new RedirectCommand(router.createUrlTree(['/nao-encontrado'])));
        }

        return of(true);
      }),
    );
};
